import React, { useState, useEffect, useCallback } from "react";
import dynamic from "next/dynamic";
import classNames from "classnames";
import { useRouter } from "next/router";
import ReactMarkdown from "markdown-to-jsx";

import type {
  BriefingType,
  FetchBriefingsParams,
} from "content-ui/briefings/types";
import type { BriefingsPageData } from "content-ui/briefings/types";
import {
  BRIEFINGS_PAGE_SIZE,
  fetchBriefingsByPage,
} from "content-ui/briefings/fetchers/briefingsFetcher";

import { trackGTMEvent, getDateFormattedNumeric } from "lib/utils";
import { useMixpanelTracking } from "lib/hooks/useMixpanelTracking";
import { useUserData } from "lib/store/user";
import ChipsFilter from "components/chipsFilter/chipsFilter";
import TextButton from "components/textButton/TextButtonLegacy";
import Button from "components/buttonNew/button";
import Spinner from "components/spinner/spinner";

import EmptyState from "./emptyState";
import { getIsArraysEqual, handleBriefingCardTracking } from "../../lib";

const FeatureCardLegacy = dynamic(
  () => import("components/featureCard/featureCard"),
  {
    ssr: false,
  }
);

export type BriefingsProps = {
  briefings: { list: BriefingType[]; count: number };
  className?: string;
  pageData: BriefingsPageData;
  params: FetchBriefingsParams;
};

const Briefings = ({
  briefings: prerenderBriefings,
  className,
  pageData,
  params: prerenderParams,
}: BriefingsProps) => {
  const { track } = useMixpanelTracking();
  const { readerSubscriptionLevel } = useUserData();
  const router = useRouter();
  const [briefings, setBriefings] = useState<BriefingType[]>(
    prerenderBriefings.list
  );
  const [count, setCount] = useState(prerenderBriefings.count);
  const [params, setParams] = useState<FetchBriefingsParams>(prerenderParams);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadMoreVisible, setIsLoadMoreVisible] = useState(
    count > params.skip + params.first
  );

  useEffect(() => {
    if (count <= params.skip + params.first) {
      setIsLoadMoreVisible(false);
    } else {
      setIsLoadMoreVisible(true);
    }
  }, [count, params]);

  const fetchNewPage = useCallback(
    async (newParams: FetchBriefingsParams) => {
      setIsLoading(true);

      const data = await fetchBriefingsByPage(newParams);

      setIsLoading(false);

      if (data.list) {
        if (getIsArraysEqual(newParams.filter, params.filter)) {
          setBriefings((prevState) => {
            return [...prevState, ...data.list];
          });
        } else {
          setBriefings(data.list);
        }
        setParams((prevState) => {
          return { ...prevState, ...newParams };
        });
      }

      setCount(data.count || 0);
    },
    [params, setBriefings, setIsLoading, setCount, setParams]
  );

  const handleLinkClick = async ({
    text,
    url,
  }: {
    text: string;
    url: string;
  }) => {
    await track("Briefings library link click", {
      "Click link": url,
      Component: "BriefingsSection",
      "Page path": router.asPath,
      "Click text": text,
    });
  };

  const handleClickOnHeaderLink = async (
    e: React.MouseEvent<HTMLHeadingElement, MouseEvent>
  ) => {
    if (e.target && (e.target as HTMLHeadingElement).getAttribute("href")) {
      await handleLinkClick({
        url: (e.target as HTMLHeadingElement).getAttribute("href") as string,
        text:
          (e.target as HTMLHeadingElement).innerText ||
          (e.target as HTMLHeadingElement).innerHTML,
      });
    }
  };

  const handleBottomLinkClick = async () => {
    await handleLinkClick({
      url: pageData.aboutBriefingsUrl,
      text: pageData.aboutBriefingsLabel,
    });
  };

  const handleBriefingClick = useCallback(
    async (briefing: BriefingType) => {
      await handleBriefingCardTracking({
        briefing,
        path: router.asPath,
        readerSubscriptionLevel,
        track,
        component: "BriefingsSection",
      });
    },
    [router.asPath, readerSubscriptionLevel, track]
  );

  const handlePagination = async () => {
    const currentPage = (params?.skip || 0) / BRIEFINGS_PAGE_SIZE;
    await fetchNewPage({
      ...params,
      skip: BRIEFINGS_PAGE_SIZE * (currentPage + 1),
    });
  };

  const handleFilterUpdate = async (filter: string[] | null) => {
    const trackingFilterData =
      filter === null ? "All markets" : filter.join(",");
    trackGTMEvent({
      event: "Briefing sector filter",
      briefingLocation: "Briefings Section",
      briefingSector: trackingFilterData,
    });

    await track("Briefing sector filter", {
      "Click text": trackingFilterData,
      "Page path": router.asPath,
      Component: "BriefingsSection",
    });

    await fetchNewPage({
      ...params,
      first: BRIEFINGS_PAGE_SIZE,
      skip: 0,
      filter: filter || [],
    });
  };

  return (
    <div className={className}>
      <div className={"mt-[52px] py-3 lg:px-[14px]"}>
        <div className={"mb-[12px] flex items-center justify-between"}>
          <h4 className={"inline-flex font-bold text-[16px]"}>
            Filter
            {isLoading && (
              <div className={"ml-3"} data-testid={"spinner"}>
                <Spinner colour={"navy"} />
              </div>
            )}
          </h4>
          <div className={"flex"}>
            <TextButton
              colour={"black"}
              component={"button"}
              onClick={async () => await handleFilterUpdate(null)}
            >
              All markets
            </TextButton>
          </div>
        </div>
        {pageData.filterList.length > 0 && (
          <ChipsFilter
            type={"multiple"}
            selected={params.filter || []}
            list={pageData.filterList.map(({ value, label }) => ({
              value,
              label,
            }))}
            brandingColor={"navy"}
            onChange={async (selected: string[]) =>
              await handleFilterUpdate(selected)
            }
          />
        )}
      </div>
      <div className="mt-[20px] flex lg:mt-[30px]">
        {briefings.length ? (
          <div className={"w-100"}>
            <ul
              aria-label={"Briefings list"}
              className={classNames(
                "mb-[70px] grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4"
              )}
              data-testid="briefing-cards"
            >
              {briefings.map((briefing: BriefingType) => (
                <li
                  key={briefing.id}
                  className={"h-[435px]"}
                  aria-label={"Briefing"}
                >
                  <FeatureCardLegacy
                    component={"link"}
                    href={briefing.url}
                    title={briefing.title}
                    wrapperClasses={"h-full"}
                    theme={"pro"}
                    excerpt={briefing.subtitle}
                    leftLabel={briefing.marketLabel}
                    rightLabel={getDateFormattedNumeric(
                      briefing.publishTimestamp
                    )}
                    backgroundImage={briefing.briefingImage}
                    withPaywall={false}
                    onClick={async () => await handleBriefingClick(briefing)}
                  />
                </li>
              ))}
            </ul>
            <div className={"flex flex-col"}>
              <div className={"mb-[30px] flex items-center justify-center"}>
                {isLoadMoreVisible && (
                  <div>
                    <Button
                      size={"small"}
                      component={"button"}
                      colour={"black"}
                      onClick={handlePagination}
                    >
                      <span
                        className={classNames("relative inline-flex", {
                          "pr-[35px]": isLoading,
                        })}
                      >
                        <span>Show more</span>{" "}
                        {isLoading && (
                          <span
                            className={"absolute right-0 top-[-2px]"}
                            data-testid={"spinner"}
                          >
                            <Spinner colour={"white"} width={20} height={20} />
                          </span>
                        )}
                      </span>
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <EmptyState
            briefingsEmptyHeading={pageData.briefingsEmptyHeading}
            briefingsEmptyDescription={pageData.briefingsEmptyDescription}
          />
        )}
      </div>
      <div className={"m-auto mb-[70px] mt-[70px] max-w-[758px] text-center"}>
        <div
          className={
            "font-recklessBold text-[32px] leading-[130%] text-black-rock"
          }
          onClick={async (e) => handleClickOnHeaderLink(e)}
        >
          <ReactMarkdown
            options={{ forceBlock: true }}
            className={
              "[&_a]:inline [&_a]:text-pro-blue [&_a]:underline [&_a]:[text-underline-position:under] focus:[&_a]:outline-none focus-visible:[&_a]:outline-none focus-visible:[&_a]:ring focus-visible:[&_a]:ring-pro-blue [&_p]:m-0 "
            }
          >
            {pageData.headingWithCta}
          </ReactMarkdown>
        </div>
        {pageData.aboutBriefingsLabel && pageData.aboutBriefingsUrl && (
          <div className={"mt-[30px]"}>
            <TextButton
              colour={"black"}
              href={pageData.aboutBriefingsUrl}
              onClick={handleBottomLinkClick}
            >
              {pageData.aboutBriefingsLabel}
            </TextButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default Briefings;
